import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Hero = () => {
  const images = [
    '/assets/images/hero-slide-1.jpg',
    '/assets/images/bg1.png',
    '/assets/images/hero-slide-3.jpg',
  ];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isWhatsAppVisible, setIsWhatsAppVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = 100; // Pixels from top to start fading
      setIsWhatsAppVisible(scrollY < threshold);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  // Generate 12 particles with random delays and positions
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    animationDelay: `${Math.random() * 6}s`,
    size: `${Math.random() * 8 + 4}px`, // Smaller particles (4-12px)
  }));

  return (
    <div className="relative w-full h-[20vh] sm:h-[60vh] overflow-hidden">
      <div className="absolute inset-0 bg-gray-200">
        {images.map((src, index) => (
          <img
            key={src}
            src={src}
            alt={`Hero Slide ${index + 1}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            loading={index === 0 ? 'eager' : 'lazy'}
            onError={() => console.error(`Failed to load slide image: ${src}`)}
          />
        ))}
      </div>
      <div className="absolute inset-0 z-[-2] pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="particle absolute rounded-full bg-[--primary-blue]/10 animate-float-up"
            style={{
              left: particle.left,
              width: particle.size,
              height: particle.size,
              animationDelay: particle.animationDelay,
            }}
          />
        ))}
      </div>
      <div className="hidden sm:flex absolute bottom-12 sm:bottom-4 left-1/2 transform -translate-x-1/2 gap-2 z-20">
        {images.map((_, index) => (
          <button
            key={index}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-[--primary-blue] scale-125' : 'bg-white/50 hover:bg-white/75'
            }`}
            onClick={() => handleDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
      <img
        className="absolute inset-0 m-auto w-full h-[40%] object-contain opacity-[0.15] z-[-1]"
        src="/assets/images/logo.jpg"
        alt="TGL Credit Ltd Logo"
        loading="lazy"
        onError={() => console.error('Failed to load logo image: /assets/images/logo.jpg')}
      />
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/50 to-black/10 z-0">
        <div className="text-center text-white px-4 sm:px-12 md:px-16 max-w-5xl">
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-inter font-extrabold mb-4 animate-fade-up tracking-tight">
            TGL Credit Limited
          </h1>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/products"
              className="group bg-[--primary-blue] text-white px-6 sm:px-8 py-2 sm:py-3 rounded-md hover:bg-[--primary-red] transition-all duration-300 font-inter text-sm sm:text-base font-medium animate-fade-up-delay uppercase tracking-wide focus:ring-2 focus:ring-[--primary-red] focus:ring-offset-2"
              aria-label="Explore Our Products"
            >
              Explore Our Products
              <svg
                className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/contact-us"
              className="bg-transparent border-2 border-white text-white px-6 sm:px-8 py-2 sm:py-3 rounded-md hover:bg-[--primary-blue] hover:border-[--primary-blue] transition-all duration-300 font-inter text-sm sm:text-base font-medium animate-fade-up-delay uppercase tracking-wide focus:ring-2 focus:ring-[--primary-blue] focus:ring-offset-2"
              aria-label="Contact Us"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
      <div
        className={`fixed bottom-6 right-6 flex items-center bg-[--primary-blue]/90 text-white px-4 py-2 rounded-full shadow-lg transition-all duration-300 z-30 hover:bg-[--primary-blue] focus:ring-2 focus:ring-[--primary-red] focus:ring-offset-2 ${
          isWhatsAppVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <Link
          to="https://wa.me/+254705194362"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-sm font-inter font-medium text-white"
          aria-label="Chat with us on WhatsApp"
        >
          <svg
            className="w-5 h-5 mr-2 fill-white hover:fill-[--primary-red] transition-colors duration-300"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M20.52 3.49A12 12 0 0 0 12 0C5.37 0 .01 5.37.01 12c0 2.12.55 4.19 1.6 6.01L0 24l6.13-1.61A12 12 0 0 0 12 24c6.63 0 12-5.37 12-12a12 12 0 0 0-3.48-8.51zM12 21.93a9.93 9.93 0 0 1-5.06-1.38l-.36-.21-3.64.96.97-3.55-.22-.37a9.93 9.93 0 0 1-1.36-5.32c0-5.51 4.49-10 10-10s10 4.49 10 10-4.49 10-10 10zm5.99-6.71c-.33-.17-1.95-.96-2.25-1.07s-.52-.17-.75.16c-.23.33-.89 1.07-1.09 1.29-.2.22-.41.25-.75.08s-1.41-.65-1.65-.76c-.24-.11-.61-.52-.61-.89s.2-.56.3-.72c.1-.16.23-.27.38-.43s.2-.33.3-.54c.1-.21.05-.38-.03-.57-.08-.18-.67-1.62-.75-1.8-.08-.18-.16-.33-.36-.33h-.62c-.21 0-.41.08-.57.23-.16.15-.57.57-.57 1.4s.6 1.62.67 1.72c.07.1 1.19 1.8 2.88 2.53.4.17.71.31.95.41.24.1.46.15.67.2.21.05.41.03.57-.03.16-.06.36-.23.46-.31.1-.08.18-.18.23-.31.05-.13.05-.28.03-.41-.02-.13-.08-.23-.13-.31z" />
          </svg>
          Chat with Us
        </Link>
      </div>
      <style>
        {`
          :root {
            --primary-blue: #2F498C;
            --primary-red: #843345;
          }
          @keyframes fade-up {
            from { opacity: 0; transform: translateY(15px) scale(0.95); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          .animate-fade-up {
            animation: fade-up 0.6s ease-out forwards;
          }
          .animate-fade-up-delay {
            animation: fade-up 0.6s ease-out 0.2s forwards;
            opacity: 0;
          }
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-fade-in {
            animation: fade-in 0.6s ease-out forwards;
          }
          @keyframes float-up {
            0% {
              transform: translateY(100vh) scale(0.9);
              opacity: 0.4;
            }
            50% {
              opacity: 0.6;
            }
            100% {
              transform: translateY(-20vh) scale(1.1);
              opacity: 0;
            }
          }
          .particle {
            animation: float-up 8s linear infinite;
            pointer-events: none;
          }
          @media (max-width: 639px) {
            .text-center {
              padding-left: 1rem;
              padding-right: 1rem;
            }
            .bottom-12 {
              bottom: 3.5rem; /* Higher to avoid WhatsApp button */
            }
            .flex-wrap {
              gap: 0.5rem; /* Tighter gap for buttons */
            }
            .px-6 {
              padding-left: 1rem;
              padding-right: 1rem;
            }
            .py-2 {
              padding-top: 0.5rem;
              padding-bottom: 0.5rem;
            }
            .text-sm {
              font-size: 0.75rem; /* 12px for button text */
            }
            .particle {
              display: none; /* Hide particles on mobile for performance */
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-fade-up, .animate-fade-up-delay, .animate-fade-in, .transition-opacity, .group-hover\\:translate-x-1, .hover\\:scale-105, .hover\\:shadow-lg, .hover\\:fill-[--primary-red], .particle, .translate-y-10 {
              animation: none;
              transform: none;
              opacity: 1;
              transition: none;
            }
            .particle {
              display: none;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Hero;